import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Chip,
  Button,
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  TextField,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  AttachMoney,
  People,
  Casino,
  EmojiEvents,
  Refresh as RefreshIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { chickenRoadAPI } from '../../../services/api';

const ChickenRoadStatistics = () => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('24h');
  
  // Date range state for custom range
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  useEffect(() => {
    fetchStatistics();
  }, [dateRange, dateFrom, dateTo]);

  // Helper to calculate dates based on range selection
  const getDateParams = () => {
    const now = new Date();
    let fromDate = new Date();
    
    switch (dateRange) {
      case '1h':
        fromDate.setHours(fromDate.getHours() - 1);
        break;
      case '24h':
        fromDate.setDate(fromDate.getDate() - 1);
        break;
      case '7d':
        fromDate.setDate(fromDate.getDate() - 7);
        break;
      case '30d':
        fromDate.setDate(fromDate.getDate() - 30);
        break;
      case 'all':
        fromDate = new Date('2020-01-01'); // Start from a very old date
        break;
      case 'custom':
        return {
          date_from: dateFrom,
          date_to: dateTo
        };
      default:
        fromDate.setDate(fromDate.getDate() - 1);
    }
    
    return {
      date_from: fromDate.toISOString().split('T')[0],
      date_to: now.toISOString().split('T')[0]
    };
  };

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const params = getDateParams();
      const response = await chickenRoadAPI.getStatistics(params);
      if (response.data.success) {
        setStatistics(response.data.statistics);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon, color = 'primary' }) => (
    <Card 
      component={motion.div}
      whileHover={{ y: -5 }}
      transition={{ type: 'spring', stiffness: 300 }}
      sx={{ height: '100%', minHeight: 140 }}
    >
      <CardContent>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box flex={1}>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ my: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: `${color}.main`, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const stats = statistics || {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            🐔 Chicken Road Statistics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Game performance metrics and analytics
          </Typography>
        </Box>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <ButtonGroup variant="outlined" size="small">
            <Button 
              onClick={() => setDateRange('1h')}
              variant={dateRange === '1h' ? 'contained' : 'outlined'}
            >
              1 Hour
            </Button>
            <Button 
              onClick={() => setDateRange('24h')}
              variant={dateRange === '24h' ? 'contained' : 'outlined'}
            >
              24 Hours
            </Button>
            <Button 
              onClick={() => setDateRange('7d')}
              variant={dateRange === '7d' ? 'contained' : 'outlined'}
            >
              7 Days
            </Button>
            <Button 
              onClick={() => setDateRange('30d')}
              variant={dateRange === '30d' ? 'contained' : 'outlined'}
            >
              30 Days
            </Button>
            <Button 
              onClick={() => setDateRange('all')}
              variant={dateRange === 'all' ? 'contained' : 'outlined'}
            >
              All Time
            </Button>
            <Button 
              onClick={() => setDateRange('custom')}
              variant={dateRange === 'custom' ? 'contained' : 'outlined'}
              startIcon={<DateRangeIcon />}
            >
              Custom
            </Button>
          </ButtonGroup>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchStatistics}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Custom Date Range Picker */}
      {dateRange === 'custom' && (
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="From Date"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="To Date"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <Button 
            variant="contained" 
            onClick={fetchStatistics}
            size="small"
          >
            Apply
          </Button>
        </Box>
      )}

      {/* Overview Statistics */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Overview
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Rounds"
            value={stats.rounds?.total || 0}
            icon={<Casino />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Bets"
            value={stats.rounds?.total_bets || 0}
            icon={<TrendingUp />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Unique Players"
            value={stats.users?.unique_players || 0}
            icon={<People />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Average Crash Point"
            value={`${stats.rounds?.avg_crash_point || '0.00'}x`}
            subtitle={`Min: ${stats.rounds?.min_crash_point || '0.00'}x | Max: ${stats.rounds?.max_crash_point || '0.00'}x`}
            icon={<Casino />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Financial Statistics */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Financial Summary
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Wagered"
            value={`₹${parseFloat(stats.rounds?.total_wagered || 0).toFixed(2)}`}
            icon={<AttachMoney />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Payout"
            value={`₹${parseFloat(stats.rounds?.total_payout || 0).toFixed(2)}`}
            icon={<AttachMoney />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="House Profit"
            value={`₹${parseFloat(stats.rounds?.house_profit || 0).toFixed(2)}`}
            icon={<AttachMoney />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="RTP"
            value={`${stats.rounds?.rtp || 0}%`}
            subtitle="Return to Player"
            icon={<TrendingUp />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Bet Status Breakdown */}
      {stats.bets && stats.bets.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
            Bet Status Breakdown
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {stats.bets.map((bet, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <StatCard
                  title={bet.status.replace('_', ' ')}
                  value={bet.count}
                  subtitle={`Total: ₹${parseFloat(bet.total_amount || 0).toFixed(2)}`}
                  icon={<Casino />}
                  color={bet.status === 'CASHED_OUT' ? 'success' : 'error'}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Top Winners */}
      {stats.top_winners && stats.top_winners.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <EmojiEvents color="warning" />
              <Typography variant="h6">
                Top Winners
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>Player</TableCell>
                    <TableCell align="right">Bet Amount</TableCell>
                    <TableCell align="right">Multiplier</TableCell>
                    <TableCell align="right">Profit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.top_winners.map((winner, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip
                          label={`#${index + 1}`}
                          color={index === 0 ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {winner.user?.username?.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2">
                            {winner.user?.username || winner.user?.display_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        ₹{parseFloat(winner.bet_amount).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${parseFloat(winner.multiplier).toFixed(2)}x`}
                          color="success"
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main" fontWeight="bold">
                          +₹{parseFloat(winner.profit).toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Recent Rounds */}
      {stats.recent_rounds && stats.recent_rounds.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent Rounds
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Round #</TableCell>
                    <TableCell align="center">Crash Point</TableCell>
                    <TableCell align="right">Total Bets</TableCell>
                    <TableCell align="right">Bet Amount</TableCell>
                    <TableCell align="right">Payout</TableCell>
                    <TableCell align="right">House Profit</TableCell>
                    <TableCell align="center">Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recent_rounds.map((round, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip label={`#${round.round_number}`} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${parseFloat(round.crash_point).toFixed(2)}x`}
                          color={parseFloat(round.crash_point) >= 2 ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">{round.total_bets}</TableCell>
                      <TableCell align="right">
                        ₹{parseFloat(round.total_bet_amount).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        ₹{parseFloat(round.total_payout_amount).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2" 
                          color={parseFloat(round.house_profit) >= 0 ? 'success.main' : 'error.main'}
                          fontWeight="bold"
                        >
                          ₹{parseFloat(round.house_profit).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={round.is_manual_crash ? 'Manual' : 'Auto'}
                          size="small"
                          color={round.is_manual_crash ? 'warning' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
};

export default ChickenRoadStatistics;
